import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, RotateCcw, ShieldCheck, ShoppingCart, Store, Truck } from 'lucide-react';
import { ShopChrome, CategoryGlyph } from '@/components/shop/ShopChrome';
import { categoryLook } from '@/lib/shopCategories';
import { EmptyState, Shimmer } from '@/components/ui';
import { getCart, getShopHome } from '@/api/shop';
import { friendlyError } from '@/lib/apiError';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import { syncCartCount, useShopState } from '@/lib/shopStore';
import { rupees } from '@/lib/shop';
import { readStoredLocation } from '@/lib/catalog';
import { useAuth } from '@/context/AuthContext';
import { readRecentlyViewed, resetShopViews, useShopEventFlush } from '@/lib/shopHome';
import { HeroCarousel, PromoBanners } from '@/components/shop/storefront/Slides';
import {
  BrandSpotlight,
  CategoryTiles,
  CollectionGrid,
  DealOfDay,
  OfferStrip,
  PersonalRail,
  PriceStore,
  ProductRailSection,
  RailSkeleton,
  SECTION_ICONS,
  StoreSpotlight,
} from '@/components/shop/storefront/Sections';

/**
 * LK Shop home: renders the admin's merchandised sections in order (hero,
 * offers, deals, quad cards, price stores, rails, banners, brands, local
 * stores), with the shopper's own rows — cart and recently viewed — slotted in
 * after the first few blocks. Unknown section types are skipped, so the admin
 * can ship a new type before the website knows it.
 */

const promises = (freeAbove) => [
  { icon: Truck, title: 'Free delivery', text: `On orders above ${rupees(freeAbove ?? 499)} per store` },
  { icon: RotateCcw, title: 'Easy returns', text: 'Return eligible items after delivery' },
  { icon: ShieldCheck, title: 'Secure payments', text: 'UPI, cards and netbanking' },
  { icon: Store, title: 'Local sellers', text: 'Stores from your own city' },
];

/** Personal rows go in after this many sections (hero, offers, categories). */
const PERSONAL_AFTER = 3;

const renderSection = (s, categories) => {
  switch (s.type) {
    case 'hero_carousel':
      return <HeroCarousel section={s} />;
    case 'offer_strip':
      return <OfferStrip section={s} />;
    case 'categories':
      return <CategoryTiles section={s} categories={categories} />;
    case 'deal_of_day':
      return <DealOfDay section={s} />;
    case 'collection_grid':
      return <CollectionGrid section={s} />;
    case 'price_store':
      return <PriceStore section={s} />;
    case 'product_rail':
    case 'auto_rail':
      return <ProductRailSection section={s} icon={SECTION_ICONS[s.data?.key]} />;
    case 'promo_banner':
      return <PromoBanners section={s} />;
    case 'brand_spotlight':
      return <BrandSpotlight section={s} />;
    case 'store_spotlight':
      return <StoreSpotlight section={s} />;
    default:
      return null;
  }
};

const KNOWN = new Set(['hero_carousel', 'offer_strip', 'categories', 'deal_of_day', 'collection_grid', 'price_store', 'product_rail', 'auto_rail', 'promo_banner', 'brand_spotlight', 'store_spotlight']);

/** The shopper's cart lines, for "Continue shopping". Empty for guests. */
const useCartLines = () => {
  const { user } = useAuth();
  const [lines, setLines] = useState([]);
  useEffect(() => {
    if (!user) {
      setLines([]);
      return undefined;
    }
    let live = true;
    getCart()
      .then((view) => {
        if (!live) return;
        syncCartCount(view);
        const items = (view?.groups || []).flatMap((g) => g.items || []);
        setLines(
          items.slice(0, 12).map((i) => ({
            key: `cart-${i.cartItemId}`,
            to: `/shop/p/${i.productSlug}${i.variantId ? `?variant=${i.variantId}` : ''}`,
            image: i.image,
            title: i.title,
            price: i.price,
            discountPercent: i.discountPercent,
            note: i.qty > 1 ? `Qty ${i.qty} in cart` : 'In your cart',
          }))
        );
      })
      .catch(() => {
        // A personal row is a nicety; the home page never fails over it.
      });
    return () => {
      live = false;
    };
  }, [user]);
  return lines;
};

const HomeSkeleton = () => (
  <>
    <div className="lk-container px-0 sm:px-6 sm:pt-4 lg:px-8 lg:pt-5">
      <Shimmer className="aspect-[4/3] w-full rounded-none sm:aspect-[2/1] sm:rounded-3xl lg:aspect-[16/5]" />
    </div>
    <div className="lk-container flex gap-2 overflow-hidden">
      {Array.from({ length: 4 }, (_, i) => (
        <Shimmer key={i} className="h-10 w-44 shrink-0 rounded-full" />
      ))}
    </div>
    <CategoryTiles loading />
    <RailSkeleton />
    <RailSkeleton />
  </>
);

const ShopHome = () => {
  const [home, setHome] = useState(null);
  const [error, setError] = useState(null);
  const { freeDeliveryAbove } = useShopState();
  const cartLines = useCartLines();
  const [recent] = useState(() =>
    readRecentlyViewed()
      .slice(0, 12)
      .map((p) => ({ key: `rv-${p.slug}`, to: `/shop/p/${p.slug}`, image: p.image, title: p.title, price: p.price, discountPercent: p.discountPercent }))
  );
  useShopEventFlush();
  useDocumentMeta({
    title: 'LK Shop — shop from local stores',
    description: 'Fashion, home, grocery and more from verified stores in your city. Secure payments, easy returns and free delivery on bigger orders.',
  });

  const load = () => {
    setError(null);
    setHome(null);
    getShopHome(readStoredLocation().zoneId)
      .then(setHome)
      .catch((err) => setError(friendlyError(err, 'The shop could not be loaded')));
  };
  useEffect(() => {
    resetShopViews();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = home?.categories || [];
  const sections = useMemo(() => {
    if (!home) return [];
    const list = (home.sections || []).filter((s) => KNOWN.has(s.type));
    if (list.length) return list;
    // Older API: categories + automatic rails only.
    return [
      { id: null, type: 'categories', data: {} },
      ...(home.rails || []).map((r) => ({ id: null, type: 'auto_rail', title: r.title, data: { key: r.key, items: r.items, viewAllLink: r.viewAllLink } })),
    ];
  }, [home]);

  const personal = (
    <React.Fragment key="personal">
      <PersonalRail title="Continue shopping" subtitle="Still in your cart" items={cartLines} icon={ShoppingCart} viewAll={{ link: '/shop/cart', label: 'Go to cart' }} />
      <PersonalRail title="Recently viewed" items={recent} icon={History} />
    </React.Fragment>
  );

  const PROMISES = promises(freeDeliveryAbove);
  const departments = categories.filter((c) => c.children?.length);

  return (
    <ShopChrome>
      <h1 className="sr-only">LK Shop — shop from local stores in your city</h1>
      {error ? (
        <div className="lk-container py-10">
          <EmptyState
            icon={Store}
            title="We couldn't open the shop"
            message={error}
            action={
              <button type="button" onClick={load} className="lk-btn-primary">
                Try again
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-2.5 pb-2 sm:space-y-5 sm:pb-4">
          {!home ? (
            <HomeSkeleton />
          ) : (
            <>
              {sections.map((s, i) => (
                <React.Fragment key={s.id ?? `${s.type}-${i}`}>
                  {renderSection(s, categories)}
                  {i === Math.min(PERSONAL_AFTER, sections.length) - 1 && personal}
                </React.Fragment>
              ))}
              {!sections.length && personal}

              {/* Every department's sub-categories, one tap away. */}
              {departments.length > 0 && (
                <section className="lk-container pt-2">
                  <h2 className="mb-3 text-[1.2rem] font-bold text-ink-900 sm:mb-4 sm:text-h2">Shop by category</h2>
                  <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
                    {departments.map((c) => {
                      const { bg } = categoryLook(c);
                      const leaves = c.children
                        .flatMap((child) => (child.children?.length ? child.children.map((g) => ({ ...g, parent: child.name })) : [child]))
                        .slice(0, 12);
                      const wide = leaves.length > 4;
                      return (
                        <div key={c.id} className={`overflow-hidden rounded-3xl border border-ink-200/70 bg-white shadow-card ${wide ? 'lg:col-span-2' : ''}`}>
                          <div className="flex items-center justify-between gap-4 px-4 py-3.5 sm:px-6 sm:py-4" style={{ background: `linear-gradient(90deg, ${bg}, #fff 70%)` }}>
                            <div className="flex min-w-0 items-center gap-3">
                              <CategoryGlyph category={c} className="h-11 w-11" iconClassName="h-6 w-6" />
                              <div className="min-w-0">
                                <h3 className="truncate text-h3 font-semibold text-ink-900">{c.name}</h3>
                                {c.description && <p className="hidden truncate text-sm text-ink-500 sm:block">{c.description}</p>}
                              </div>
                            </div>
                            <Link to={`/shop/c/${c.slug}`} className="lk-btn-ghost min-h-[40px] shrink-0 px-4 py-2">
                              View all
                            </Link>
                          </div>
                          <div className={`grid grid-cols-3 gap-2 p-3 sm:grid-cols-4 sm:gap-3 sm:p-5 ${wide ? 'lg:grid-cols-8' : ''}`}>
                            {leaves.map((s) => (
                              <Link
                                key={s.id}
                                to={`/shop/c/${s.slug}`}
                                className="lk-focus group flex flex-col items-center gap-2 rounded-2xl p-2 text-center transition hover:bg-surface-muted sm:p-3"
                              >
                                <CategoryGlyph category={s} className="h-14 w-14 transition-transform group-hover:scale-105 sm:h-16 sm:w-16" iconClassName="h-7 w-7" />
                                <span className="text-xs font-medium leading-tight text-ink-900 sm:text-sm">
                                  {s.name}
                                  {s.parent && <span className="block text-[11px] font-normal text-ink-500">{s.parent}</span>}
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}

          {/* Trust promises. */}
          <div className="lk-container">
            <ul className="grid grid-cols-2 gap-x-3 gap-y-4 rounded-3xl border border-ink-200/70 bg-white p-4 shadow-card sm:grid-cols-4 sm:gap-4 sm:p-6">
              {PROMISES.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex items-start gap-2.5 sm:items-center">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand-secondary">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="min-w-0 leading-tight">
                    <span className="block text-sm font-semibold text-ink-900">{title}</span>
                    <span className="mt-0.5 block text-xs text-ink-500">{text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </ShopChrome>
  );
};

export default ShopHome;
